/* @flow */
import { Observable } from 'rxjs';
import { List, fromJS } from 'immutable';
import { pipe, map, prop } from 'ramda';
import { get, getQueryParams } from 'trustar/utils';
import { stringify } from 'querystring';
import { combineEpics } from 'redux-observable';
import { displayOptions } from './getters';
import { fetch } from './actions';
import createDebugger from 'debug';

const debug = createDebugger('app:modules:companies:epics'); // eslint-disable-line no-unused-vars

import {
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  SAVE,
  SAVE_SUCCESS,
  SAVE_FAILURE,
  DELETE,
  DELETE_SUCCESS,
  DELETE_FAILURE,
  SAVE_QUOTA,
  SAVE_QUOTA_SUCCESS,
  SAVE_QUOTA_FAILURE,
  RESET_QUOTA
} from './action-types.js';

import { EnclavePermission, Quota } from './types.js';

/**
 * NOTE: This is necessary because depending on whether or not a save came from
 * company creation or editing the payload will be immutable or a plain object.
 */
const getRateLimit = pipe(
  get('rateLimit'),
  Number,
  x => (x ? x : undefined) // We allow rate limite to be undefined, for now. This particular case guards against NaN
);

const fromJSON = company => {
  company = fromJS(company);
  return company.mapEntries(([k, v]) => {
    switch (k) {
    case 'enclaves':
      return [k, v.toMap().mapKeys((k1, v1) => v1.get('id'))];
    default:
      return [k, v];
    }
  });
};

const toJSON = (company, store) => {
  return fromJS(company)
    .update('maxIngestEmailsCount', Number)
    .update('enclaves', v => (v ? v.map(EnclavePermission).toSet() : []))
    .update('sector', sector =>
      store
        .getState()
        .getIn(['sectors', 'sectors', Number(sector.get('value'))])
    )
    .update('sharingGroups', groups =>
      groups.map(group => ({ guid: group.guid }))
    )
    .toJS();
};

export const ENDPOINT = '/services/companies';

const fetchEpic = (action$, store, { ajax }) =>
  action$
    .ofType(FETCH)
    .map(prop('payload'))
    .debounceTime(500)
    .switchMap(params => {
      const parameterizedUrl = ENDPOINT + '?' + parameterizeQueryString(params);
      return ajax
        .get(parameterizedUrl)
        .map(({ response }) => {
          const { items, ...meta } = response;
          return {
            type: FETCH_SUCCESS,
            payload: items.map(fromJSON),
            meta: {
              ...meta,
              empty: !!response.empty,
              pageNumber: response.pageNumber || params.pageNumber,
              pageSize: response.pageSize || params.pageSize,
              hasNext: !!response.hasNext
            }
          };
        })
        .catch(err =>
          Observable.of({
            type: FETCH_FAILURE,
            error: true,
            payload: err
          })
        );
    });

/**
 * NOTE: We're in an odd state here, because we want to allow admins to update
 * or set a rate limit when editing or creating a company, so the rate limit is
 * kept in the same state as all the other editable company data. However, the
 * rate limiting api is separate and requires a separate request. Therefore we
 * need to hold on to this rate limit value and pass it through so that another
 * epic can make use of it to handle the company rate limit request.
 */
const saveEpic = (action$, store, { ajax }) =>
  action$
    .ofType(SAVE)
    .map(prop('payload'))
    .map(x => toJSON(x, store))
    .mergeMap(data => {
      let requestType;
      const companyGuid = data.guid;
      if (companyGuid) {
        requestType = ajax.putJSON(`${ENDPOINT}/${companyGuid}`, data);
      } else {
        requestType = ajax.postJSON(ENDPOINT, data);
      }
      return requestType
        .map(prop('response'))
        .map(fromJSON)
        .map(payload => ({
          type: SAVE_SUCCESS,
          payload,
          meta: {
            alert: { type: 'success', text: 'Company saved.' },
            rateLimit: getRateLimit(data) // See NOTE
          }
        }))
        .catch(err =>
          Observable.of({
            type: SAVE_FAILURE,
            error: true,
            payload: err,
            meta: {
              alert: { type: 'error', text: 'Could not save company.' }
            }
          })
        );
    });

const deleteEpic = (action$, store, { ajax }) =>
  action$.ofType(DELETE).mergeMap(x =>
    ajax
      .delete(`${ENDPOINT}/${String(x.payload)}`)
      .map(({ response }) => ({
        type: DELETE_SUCCESS,
        payload: response,
        meta: {
          alert: { type: 'success', text: 'Company removed.' }
        }
      }))
      .catch(err =>
        Observable.of({
          type: DELETE_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: { type: 'error', text: 'Could not remove company.' }
          }
        })
      )
  );

/**
 * Array<Object> -> List<Quota>
 */
const toQuotaList = pipe(
  List,
  map(Quota)
);

/**
 * Save (update or create) a company API request quota
 */
const saveQuotaEpic = (action$, store, { ajax }) => {
  // Requests to update a quota directly
  const updateQuota$ = action$
    .ofType(SAVE_QUOTA)
    .map(prop('payload'))
    .map(payload => ({
      alert: { type: 'success', text: 'Quota updated successfully' }, // Add the alert
      ...payload
    }));

  return Observable.merge(
    updateQuota$
    // savedCompanyQuota$,
  ).mergeMap(({ companyId, counterId, params, alert }) => {
    const isUpdate = !!counterId;
    const req$ = isUpdate
      ? ajax.putJSON(
          `${ENDPOINT}/${companyId}/request-quotas/${counterId}`,
          params
        ) // Update
      : ajax.postJSON(`${ENDPOINT}/${companyId}/request-quotas`, params); // Create

    return req$
      .map(prop('response'))
      .map(x => [x])
      .map(toQuotaList)
      .map(list => ({ [companyId]: list }))
      .map(quotas => ({
        type: SAVE_QUOTA_SUCCESS,
        payload: quotas,
        meta: { alert }
      }))
      .catch(err =>
        Observable.of({
          type: SAVE_QUOTA_FAILURE,
          error: true,
          payload: err,
          meta: { alert }
        })
      );
  });
};

/**
 * Trigger a re-fetch of companies with the currently specified options. For
 * example, when a new company is saved rather than tack it on to state we need to
 * refetch. The reason is, all the logic related to sorting and filtering lives
 * on the server side, so we need to the server to provide us with an updated
 * list of companies based on our current criteria.
 */
const refetchEpic = (action$, store) =>
  action$
    .ofType(SAVE_SUCCESS)
    .map(() => displayOptions(store.getState()))
    .map((params) => {
      const defaultParams = getQueryParams(params);

      return {
        ...defaultParams,
        enclaveId: params.enclaveId,
      };
    })
    .map(fetch);

/**
 * NOTE: There is no specific endpoint for this, so it needs to do the same
 * thing as the save action but only setting usedRequests to zero
 */
const resetQuotaEpic = (action$, store, { ajax }) =>
  action$
    .ofType(RESET_QUOTA)
    .map(prop('payload'))
    .map(({ companyId, quotaId }) => ({
      type: SAVE_QUOTA,
      payload: {
        companyId,
        counterId: quotaId,
        params: {
          usedRequests: 0 // This is they key here. Setting used requests to zero
        }
      }
    }));

export default combineEpics(
  fetchEpic,
  refetchEpic,
  saveEpic,
  deleteEpic,
  saveQuotaEpic,
  resetQuotaEpic
);
function parameterizeQueryString(params) {
  const { sortBy, sortOrder, ...otherParams } = params;
  const queryString = {
    ...otherParams,
    sort: sortBy && sortOrder ? `${sortBy},${sortOrder}` : 'name,ASC'
  };
  return stringify(queryString);
}



// WEBPACK FOOTER //
// ./src/app/modules/companies/epics.js